// Helpers for the Activity-wise view (one row per candidate stage).
// Rows come from POST jobs/{jobId}/job-candidates/stage-rows/search; filtering,
// sorting and paging all happen on the server.

import { HIRING_STATUS_COLORS, getStatusVisual } from "../../../../utils/candidatesV2Constants";

// Default rows per page; must be one of the shared pagination's per-page options.
export const ACTIVITY_PAGE_SIZE = 10;
export const SEARCH_DEBOUNCE_MS = 400;

export const UNASSIGNED = "__unassigned__";
export const YET_TO_START = "Yet to start";
export const DEFAULT_HIRING_STATUS = "In Pipeline";

export const CUSTOM_PERIOD = "custom";

export const PERIODS = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "Last 7 days", days: 7 },
  { value: "15d", label: "Last 15 days", days: 15 },
  { value: CUSTOM_PERIOD, label: "Custom" },
];

// Which stage date the Period (from_date / to_date) applies to → payload date_field.
export const DEFAULT_DATE_FIELD = "assigned";
export const DATE_FIELDS = [
  { value: "assigned", label: "Assigned", payload: "assigned_at" },
  { value: "completed", label: "Completed", payload: "completed_at" },
];

export const EMPTY_ACTIVITY_FILTERS = {
  dateField: DEFAULT_DATE_FIELD,
  period: "all",
  dateRange: [null, null], // [from, to] picked in the toolbar when period is "custom"
  search: "",
  stages: [],
  stageStatuses: [], // "stageId::statusId" pairs, picked per stage like Advanced Search
  hiringStatuses: [],
  recruiters: [],
};

// A Stage status pick is a (stage, status) pair, the same as in Advanced Search.
const PAIR_SEP = "::";
export const stageStatusKey = (stageId, statusId) => `${stageId}${PAIR_SEP}${statusId}`;
const splitStageStatusKey = (key) => key.split(PAIR_SEP);

// Table column → API order_by.
const ORDER_BY = { assigned: "assigned_at", completed: "completed_at" };

// ── Dates ────────────────────────────────────────────────────────────────────

const startOfDay = (date, offsetDays = 0) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offsetDays);
  return d;
};

// Inclusive [from, to] in local time for the selected period, or null when no date filter applies.
// A custom range with only a start date covers just that day.
const periodRange = ({ period, dateRange }, now = new Date()) => {
  const endOf = (d) => new Date(startOfDay(d, 1).getTime() - 1);
  if (period === "today") return [startOfDay(now), endOf(now)];
  if (period === "yesterday") return [startOfDay(now, -1), endOf(startOfDay(now, -1))];
  if (period === CUSTOM_PERIOD) {
    const [start, end] = dateRange || [];
    if (!start) return null;
    // Always older date → from_date, newer → to_date, whichever order they were picked in.
    const [older, newer] = [start, end || start].sort((x, y) => new Date(x) - new Date(y));
    return [startOfDay(older), endOf(newer)];
  }
  const p = PERIODS.find((x) => x.value === period);
  return p?.days ? [startOfDay(now, -(p.days - 1)), endOf(now)] : null;
};

export const isPeriodActive = (f) => f.period !== "all" && (f.period !== CUSTOM_PERIOD || !!f.dateRange?.[0]);

// ── Request payload ──────────────────────────────────────────────────────────

export const buildStageRowsPayload = (f, { search, sort, page, pageSize }) => {
  const payload = {
    page,
    page_size: pageSize,
    order_by: ORDER_BY[sort.field] || ORDER_BY.assigned,
    order_direction: sort.dir,
    include_unassigned: f.recruiters.includes(UNASSIGNED),
    date_field: (DATE_FIELDS.find((d) => d.value === f.dateField) || DATE_FIELDS[0]).payload,
  };
  const q = search.trim();
  if (q) payload.search = q;
  // The API takes flat id lists, so each (stage, status) pick becomes its status id plus its
  // stage id. An explicit Stage filter wins over the stages implied by status picks.
  const pairs = f.stageStatuses.map(splitStageStatusKey);
  const pairStages = [...new Set(pairs.map(([stageId]) => stageId))];
  const statusIds = [...new Set(pairs.map(([, statusId]) => statusId))];
  const stageIds = f.stages.length ? f.stages : pairStages;
  if (stageIds.length) payload.hiring_stage_ids = stageIds;
  if (statusIds.length) payload.stage_status_ids = statusIds;
  if (f.hiringStatuses.length) payload.hiring_status_ids = f.hiringStatuses;
  const recruiterIds = f.recruiters.filter((id) => id !== UNASSIGNED);
  if (recruiterIds.length) payload.assigned_company_user_ids = recruiterIds;
  const range = periodRange(f);
  if (range) {
    payload.from_date = range[0].toISOString();
    payload.to_date = range[1].toISOString();
  }
  return payload;
};

// Filters in the left sidebar (Period sits in the toolbar above the table).
export const countActiveActivityFilters = (f) =>
  (f.dateField !== DEFAULT_DATE_FIELD ? 1 : 0) + (f.search.trim() ? 1 : 0) +
  f.stages.length + f.stageStatuses.length + f.hiringStatuses.length + f.recruiters.length;

// ── Response rows ────────────────────────────────────────────────────────────

// "IN_PROGRESS" / "In Progress" → "In progress", to match the badge copy in the design.
export const formatStageStatus = (raw) => {
  if (!raw) return null;
  const s = String(raw).replace(/_/g, " ").trim().toLowerCase();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : null;
};

export const stageStatusTone = (label) => {
  const s = (label || "").toLowerCase();
  if (s.includes("complet") || s.includes("pass")) return "success";
  if (s.includes("fail") || s.includes("expir") || s.includes("reject") || s.includes("cancel")) return "danger";
  if (s.includes("schedul")) return "info";
  if (s === YET_TO_START.toLowerCase() || s === "not started") return "neutral";
  return "warning";
};

export const hiringStatusColor = (name, color) => color || HIRING_STATUS_COLORS[name] || getStatusVisual(name).color;

// Maps one stage-row from the API onto what the table renders. Field names are read
// defensively (flat or nested) until the response shape is confirmed.
export const normalizeStageRow = (raw, index) => {
  const c = raw.candidate || {};
  const candidateId = raw.job_candidate_id || raw.candidate_id || c.id;
  const stageId = raw.hiring_stage_id || raw.hiring_stage?.id || raw.stage?.id;
  const statusLabel = formatStageStatus(raw.stage_status_name || raw.stage_status?.name || raw.status?.name) || YET_TO_START;
  const completedAt = raw.completed_at || null;
  return {
    key: raw.id || raw.job_candidate_hiring_stage_id || `${candidateId}:${stageId}:${index}`,
    // Shaped like a candidate so useCandidateAccess can check it.
    candidate: {
      ...c,
      id: candidateId,
      name: raw.candidate_name || c.name || raw.name,
      phone_number: raw.phone_number || raw.candidate_phone || c.phone_number,
      assigned_company_user_id: raw.assigned_company_user_id ?? c.assigned_company_user_id,
      assigned_user_id: raw.assigned_user_id ?? c.assigned_user_id,
    },
    stageName: raw.hiring_stage_name || raw.stage_name || raw.hiring_stage?.name || raw.stage?.name || "—",
    assignedAt: raw.assigned_at || null,
    completedAt,
    completed: /complet/i.test(statusLabel) || !!completedAt,
    statusLabel,
    hiringStatus: raw.hiring_status_name || raw.hiring_status?.name || DEFAULT_HIRING_STATUS,
    hiringStatusColor: raw.hiring_status_color || raw.hiring_status?.color || null,
  };
};

// ── Filter options ───────────────────────────────────────────────────────────

const optionId = (o) => String(o.id ?? o.value ?? "");

const isNotAssigned = (label) => /^not\s*assigned$/i.test(String(label || "").trim());

// From the advanced-filters `stage_statuses` subitems: each option is a stage whose
// meta.status_options lists its statuses. Stages → Stage filter; stages with their statuses
// (minus "Not assigned") → the grouped Stage status filter.
export const mapStageStatusSubitems = (data) => {
  const stages = data?.options || [];
  return {
    stages: stages.map((s) => ({ value: optionId(s), label: s.label })),
    stageStatuses: stages.map((s) => {
      const stageId = optionId(s);
      return {
        value: stageId,
        label: s.label,
        count: s.count ?? null,
        statuses: (s.meta?.status_options || [])
          .filter((opt) => optionId(opt) && !isNotAssigned(opt.label))
          .map((opt) => ({
            value: stageStatusKey(stageId, optionId(opt)),
            label: opt.label,
            color: opt.color || null,
            count: opt.count ?? null,
          })),
      };
    }),
  };
};

// From the advanced-filters `hiring_status` subitems (checkbox_list).
export const mapHiringStatusSubitems = (data) =>
  (data?.options || []).map((o) => ({ value: optionId(o), label: o.label, color: hiringStatusColor(o.label, o.color) }));

export const mapRecruiters = (list) => [
  { value: UNASSIGNED, label: "Unassigned" },
  ...list
    .map((r) => ({
      value: String(r.company_user_id ?? r.assigned_company_user_id ?? r.id ?? ""),
      label: r.name || r.full_name || r.user_name || r.email || "Unknown",
      count: r.count ?? r.stage_count ?? null,
      userId: r.user_id != null ? String(r.user_id) : null, // lets insights links (keyed by user_id) match
    }))
    .filter((r) => r.value),
];

// ── Deep links from Job Insights ─────────────────────────────────────────────
// /jobs/:id/candidates?view=activity&stage=…&rowStatus=…&hiring=…&recruiter=…&from=YYYY-MM-DD&to=YYYY-MM-DD
// Insights ids don't always match the filter option ids, so links are resolved against
// the loaded options (by id first, then by label) before the first request goes out.

export const DRILL_PARAMS = ["stage", "rowStatus", "hiring", "recruiter", "recruiterName", "from", "to"];

// Insights row-status keys → stage status label patterns.
const ROW_STATUS_PATTERNS = {
  done: /complet/i,
  "in-progress": /in[\s_-]*progress/i,
  pending: /pending/i,
  expired: /expir/i,
};

export const parseDrillParams = (searchParams) => {
  if (!DRILL_PARAMS.some((k) => searchParams.get(k))) return null;
  return {
    stageIds: (searchParams.get("stage") || "").split(",").filter(Boolean),
    rowStatus: searchParams.get("rowStatus") || "",
    hiring: searchParams.get("hiring") || "",
    recruiter: searchParams.get("recruiter") || "",
    recruiterName: searchParams.get("recruiterName") || "",
    from: searchParams.get("from") || "",
    to: searchParams.get("to") || "",
  };
};

// Which option lists must be loaded before `drill` can be turned into filters.
export const drillOptionKeys = (drill) => [
  drill.stageIds.length && "stages",
  drill.rowStatus && "stageStatuses",
  drill.hiring && "hiringStatuses",
  drill.recruiter && "recruiters",
].filter(Boolean);

const parseLocalDate = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || "");
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

const matchValues = (options, predicate) => (options || []).filter(predicate).map((o) => o.value);

export const resolveDrillFilters = (drill, options) => {
  const f = { ...EMPTY_ACTIVITY_FILTERS };

  if (drill.stageIds.length) {
    const matched = matchValues(options.stages, (o) => drill.stageIds.includes(o.value));
    f.stages = matched.length ? matched.slice(0, 1) : drill.stageIds.slice(0, 1);
  }

  const statusPattern = ROW_STATUS_PATTERNS[drill.rowStatus];
  if (statusPattern) {
    // Limit to the linked stage when there is one; otherwise that status on every stage.
    const groups = f.stages.length
      ? (options.stageStatuses || []).filter((g) => f.stages.includes(g.value))
      : options.stageStatuses || [];
    f.stageStatuses = groups.flatMap((g) => matchValues(g.statuses, (o) => statusPattern.test(o.label || "")));
  }

  if (drill.hiring) {
    const h = drill.hiring;
    const byId = matchValues(options.hiringStatuses, (o) => o.value === h);
    const byLabel = h === "hired"
      ? matchValues(options.hiringStatuses, (o) => /^hired$/i.test(o.label || ""))
      : h === "none"
        ? matchValues(options.hiringStatuses, (o) => /^(in pipeline|in progress)$/i.test(o.label || ""))
        : [];
    f.hiringStatuses = byId.length ? byId : byLabel.length ? byLabel : (h !== "hired" && h !== "none" ? [h] : []);
  }

  if (drill.recruiter) {
    if (drill.recruiter === "unassigned") {
      f.recruiters = [UNASSIGNED];
    } else {
      const name = drill.recruiterName.trim().toLowerCase();
      const match = (options.recruiters || []).find((o) =>
        o.value === drill.recruiter || o.userId === drill.recruiter || (name && String(o.label).toLowerCase() === name));
      f.recruiters = [match ? match.value : drill.recruiter];
    }
  }

  const from = parseLocalDate(drill.from);
  const to = parseLocalDate(drill.to);
  if (from) {
    f.period = CUSTOM_PERIOD;
    f.dateRange = [from, to || from];
  }
  return f;
};

export const formatActivityDate = (d) =>
  new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
